import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, fmtMoney, formatApiErrorDetail } from "@/lib/api";
import { DoorOpen, MapPin, BedDouble, Bath, Square, Calendar, Loader2, CheckCircle2, ArrowRight } from "lucide-react";
import BuyerSignupPanel from "@/components/BuyerSignupPanel";

const STRATEGY_LABEL = {
  wholesale: "Wholesale", flip: "Fix & Flip", brrrr: "BRRRR",
  rental: "Buy & Hold", novation: "Novation", sub_to: "Sub-To",
  seller_finance: "Seller Finance",
};

export default function PublicDealRoom() {
  const { slug } = useParams();
  const [room, setRoom] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [heroIdx, setHeroIdx] = useState(0);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/deals/${slug}`)
      .then((r) => setRoom(r.data.room))
      .catch(() => setError("This deal isn't available anymore."))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <FullScreen><Loader2 size={20} className="animate-spin" /> Loading deal…</FullScreen>;
  if (error || !room) return <FullScreen>{error || "Deal not found"}</FullScreen>;

  const photos = room.photo_urls || [];
  const heroPhoto = photos[heroIdx] || photos[0];
  const accent = room.operator?.brand_accent || "#F59E0B";

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-[var(--ink,#0F172A)]" data-testid="public-deal-room">
      {/* Operator top bar */}
      <header className="border-b border-[var(--line,#E2E8F0)] bg-white">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {room.operator?.brand_logo ? (
              <img src={room.operator.brand_logo} alt="" className="h-8 w-auto" />
            ) : (
              <DoorOpen size={18} style={{ color: accent }} />
            )}
            <span className="font-display font-extrabold text-lg">{room.operator?.display_name || "Deal Room"}</span>
          </div>
          <Link to="/" className="text-xs text-[var(--ink-3,#94A3B8)] hover:text-[var(--ink,#0F172A)]">Powered by iSpeedToClose →</Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 grid gap-8 lg:grid-cols-[1.5fr_1fr]">
        {/* LEFT COLUMN — property showcase */}
        <div className="space-y-6">
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--ink-3,#94A3B8)] mb-2 flex items-center gap-1.5">
              <MapPin size={12} />
              {[room.city, room.state, room.zip_code].filter(Boolean).join(", ") || room.property_address}
            </div>
            <h1 className="font-display font-black text-4xl md:text-5xl tracking-tight leading-[1.05]" data-testid="public-deal-title">
              {room.title}
            </h1>
            <div className="mt-2 text-sm text-[var(--ink-2,#475569)]">{room.property_address}</div>
          </div>

          {photos.length > 0 && (
            <div>
              <div className="aspect-[16/10] rounded-xl overflow-hidden bg-[var(--line,#E2E8F0)] border border-[var(--line,#E2E8F0)]">
                <img src={heroPhoto} alt="" className="w-full h-full object-cover" data-testid="public-deal-hero-photo" />
              </div>
              {photos.length > 1 && (
                <div className="mt-3 grid grid-cols-6 gap-2">
                  {photos.slice(0, 12).map((p, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setHeroIdx(i)}
                      className={`aspect-square rounded overflow-hidden border-2 ${i === heroIdx ? "border-[var(--ink,#0F172A)]" : "border-transparent"}`}
                      data-testid={`public-deal-thumb-${i}`}
                    >
                      <img src={p} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {room.rehab_render && (
            <RehabBeforeAfter render={room.rehab_render} accent={accent} />
          )}

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Spec icon={BedDouble} label="Beds" value={room.beds} />
            <Spec icon={Bath} label="Baths" value={room.baths} />
            <Spec icon={Square} label="Sq ft" value={room.sqft?.toLocaleString?.() || room.sqft} />
            <Spec icon={Calendar} label="Year" value={room.year_built} />
          </div>

          {(room.exit_strategies || []).length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-widest text-[var(--ink-3,#94A3B8)] mb-2">Exit strategies</div>
              <div className="flex flex-wrap gap-2">
                {room.exit_strategies.map((s) => (
                  <span key={s} className="px-3 py-1 rounded-full text-xs font-semibold border" style={{ borderColor: accent, color: accent }}>
                    {STRATEGY_LABEL[s] || s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {room.description && (
            <div>
              <div className="text-xs uppercase tracking-widest text-[var(--ink-3,#94A3B8)] mb-2">The deal</div>
              <div className="text-[15px] leading-relaxed text-[var(--ink-2,#475569)] whitespace-pre-wrap" data-testid="public-deal-description">{room.description}</div>
            </div>
          )}

          {room.comps?.avm && (
            <div className="rounded-lg border border-[var(--line,#E2E8F0)] bg-white p-4">
              <div className="text-xs uppercase tracking-widest text-[var(--ink-3,#94A3B8)] mb-1">RentCast AVM</div>
              <div className="font-display font-bold text-2xl">
                {fmtMoney(room.comps.avm?.price ?? room.comps.avm?.value ?? room.comps.avm?.priceEstimate)}
              </div>
              <div className="text-xs text-[var(--ink-3,#94A3B8)] mt-0.5">
                Automated valuation · {room.comps.fetched_at?.split("T")[0]}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT COLUMN — pricing + offer form */}
        <div className="space-y-4">
          <div className="rounded-xl border border-[var(--line,#E2E8F0)] bg-white p-6 shadow-sm sticky top-6">
            <div className="text-xs uppercase tracking-widest text-[var(--ink-3,#94A3B8)]">Asking</div>
            <div className="font-display font-black text-4xl" data-testid="public-deal-asking-price">{fmtMoney(room.asking_price)}</div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-[var(--ink-3,#94A3B8)]">ARV</span><div className="font-semibold">{fmtMoney(room.arv)}</div></div>
              <div><span className="text-[var(--ink-3,#94A3B8)]">Repairs</span><div className="font-semibold">{fmtMoney(room.repair_estimate)}</div></div>
              {room.emd_required != null && (
                <div><span className="text-[var(--ink-3,#94A3B8)]">EMD</span><div className="font-semibold">{fmtMoney(room.emd_required)}</div></div>
              )}
              {room.close_by && (
                <div><span className="text-[var(--ink-3,#94A3B8)]">Offers by</span><div className="font-semibold">{room.close_by}</div></div>
              )}
            </div>

            <div className="mt-5 pt-5 border-t border-[var(--line,#E2E8F0)]">
              {submitted ? (
                <div className="text-center py-4" data-testid="public-offer-success">
                  <CheckCircle2 size={40} className="text-emerald-600 mx-auto mb-2" />
                  <div className="font-display font-bold text-lg">Offer submitted 🎯</div>
                  <div className="text-sm text-[var(--ink-2,#475569)] mt-1">{room.operator?.display_name} will reach out shortly.</div>
                </div>
              ) : (
                <OfferForm slug={slug} accent={accent} onSuccess={() => setSubmitted(true)} />
              )}
            </div>
          </div>

          {/* Buyer signup — turn every Deal Room view into a compounding buyer-list ping */}
          <BuyerSignupPanel
            slug={slug}
            accent={accent}
            defaultState={room.state}
            defaultStrategy={(room.exit_strategies || [])[0]}
          />
        </div>
      </main>

      <footer className="border-t border-[var(--line,#E2E8F0)] bg-white mt-16">
        <div className="max-w-6xl mx-auto px-6 py-8 text-xs text-[var(--ink-3,#94A3B8)] text-center space-y-1">
          <div>Deal Room powered by <Link to="/" className="underline font-semibold">iSpeedToClose</Link></div>
          <div>Wholesale opportunity marketed by {room.operator?.display_name}. Buyer is responsible for due diligence — see our <Link to="/legal/disclaimer" className="underline">Disclaimer</Link>.</div>
        </div>
      </footer>
    </div>
  );
}

function OfferForm({ slug, accent, onSuccess }) {
  const [buyer_name, setName] = useState("");
  const [buyer_email, setEmail] = useState("");
  const [buyer_phone, setPhone] = useState("");
  const [offer_price, setPrice] = useState("");
  const [emd_amount, setEmd] = useState("");
  const [close_date, setClose] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/public/deals/${slug}/offers`, {
        buyer_name,
        buyer_email,
        buyer_phone: buyer_phone || undefined,
        offer_price: Number(offer_price),
        emd_amount: emd_amount ? Number(emd_amount) : undefined,
        close_date: close_date || undefined,
        notes: notes || undefined,
      });
      onSuccess();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Could not submit offer");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" data-testid="public-offer-form">
      <div className="text-xs uppercase tracking-widest text-[var(--ink-3,#94A3B8)]">Make an offer</div>
      <input required placeholder="Full name *" value={buyer_name} onChange={(e) => setName(e.target.value)} className="input" data-testid="public-offer-name" />
      <input required type="email" placeholder="Email *" value={buyer_email} onChange={(e) => setEmail(e.target.value)} className="input" data-testid="public-offer-email" />
      <input placeholder="Phone" value={buyer_phone} onChange={(e) => setPhone(e.target.value)} className="input" data-testid="public-offer-phone" />
      <input required type="number" min="1" placeholder="Offer price ($) *" value={offer_price} onChange={(e) => setPrice(e.target.value)} className="input" data-testid="public-offer-price" />
      <div className="grid grid-cols-2 gap-2">
        <input type="number" min="0" placeholder="EMD ($)" value={emd_amount} onChange={(e) => setEmd(e.target.value)} className="input" data-testid="public-offer-emd" />
        <input placeholder="Close date" value={close_date} onChange={(e) => setClose(e.target.value)} className="input" data-testid="public-offer-close" />
      </div>
      <textarea placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} className="input min-h-[70px] text-sm" data-testid="public-offer-notes" />
      <button
        type="submit"
        disabled={loading}
        className="w-full flex items-center justify-center gap-2 py-3 rounded-lg font-semibold text-sm"
        style={{ background: accent, color: "#0F172A" }}
        data-testid="public-offer-submit"
      >
        {loading ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
        {loading ? "Submitting…" : "Submit offer"}
      </button>
      <p className="text-[10px] text-[var(--ink-3,#94A3B8)] leading-relaxed">
        Submitting an offer is a non-binding expression of interest. Final terms confirmed via a written assignment or purchase agreement.
      </p>
      <a
        href="/funding?source=deal-room-offer"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-1 inline-flex items-center gap-1.5 text-[11px] font-semibold text-[var(--ink-3,#94A3B8)] hover:text-[#F59E0B] transition"
        data-testid="public-offer-pof-link"
      >
        Need a Proof-of-Funds letter? Get one fast from our lenders →
      </a>
    </form>
  );
}

function Spec({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-[var(--line,#E2E8F0)] bg-white p-3 flex items-center gap-3">
      <Icon size={18} className="text-[var(--ink-3,#94A3B8)]" />
      <div>
        <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3,#94A3B8)]">{label}</div>
        <div className="font-display font-bold text-lg leading-none mt-0.5">{value ?? "—"}</div>
      </div>
    </div>
  );
}

function FullScreen({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center gap-2 text-[var(--ink-2,#475569)]">
      {children}
    </div>
  );
}

/**
 * RehabBeforeAfter — interactive slider showing an AI-rendered vision-cast
 * next to the as-listed photo. Drag or click the slider to reveal how the
 * property could look post-rehab (Gemini Nano Banana render).
 *
 * The two image URLs come back on the public /deals/{slug} payload as
 * `room.rehab_render.before_url` and `.after_url` — served by our
 * backend so we never leak the raw base64 payload.
 */
function RehabBeforeAfter({ render, accent }) {
  const [pos, setPos] = useState(50); // 0-100, % of after visible on the left
  const beforeUrl = `${API.replace(/\/api$/, "")}${render.before_url}`;
  const afterUrl = `${API.replace(/\/api$/, "")}${render.after_url}`;
  return (
    <div data-testid="public-deal-rehab-render">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--ink-3,#94A3B8)] mb-0.5">
            AI After-Rehab Preview
          </div>
          <div className="font-display font-bold text-lg leading-tight">
            See what it&apos;ll look like after rehab.
          </div>
        </div>
        <span
          className="text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded"
          style={{ background: accent, color: "#0B1D3B" }}
        >
          Vision-cast
        </span>
      </div>

      <div className="relative aspect-[16/10] rounded-xl overflow-hidden border border-[var(--line,#E2E8F0)] bg-slate-900 select-none">
        {/* AFTER — sits underneath as full backdrop */}
        <img
          src={afterUrl}
          alt="After rehab"
          className="absolute inset-0 w-full h-full object-cover"
          data-testid="rehab-render-after-img"
          draggable={false}
        />
        {/* BEFORE — clipped to the left of the slider */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        >
          <img
            src={beforeUrl}
            alt="Before"
            className="absolute inset-0 w-full h-full object-cover"
            data-testid="rehab-render-before-img"
            draggable={false}
          />
        </div>
        {/* Slider handle */}
        <div
          className="absolute top-0 bottom-0 w-1 bg-white shadow-[0_0_10px_rgba(0,0,0,0.5)]"
          style={{ left: `calc(${pos}% - 2px)` }}
        >
          <div
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-10 w-10 rounded-full bg-white shadow-lg flex items-center justify-center"
            style={{ left: "50%" }}
          >
            <div className="flex items-center gap-0.5" style={{ color: accent }}>
              <span className="text-lg leading-none">‹</span>
              <span className="text-lg leading-none">›</span>
            </div>
          </div>
        </div>
        {/* Corner labels */}
        <div className="absolute top-3 left-3 text-[10px] uppercase tracking-widest font-black text-white bg-black/40 px-2 py-1 rounded backdrop-blur-sm">
          Before
        </div>
        <div
          className="absolute top-3 right-3 text-[10px] uppercase tracking-widest font-black px-2 py-1 rounded backdrop-blur-sm"
          style={{ background: `${accent}CC`, color: "#0B1D3B" }}
        >
          After · AI Vision-Cast
        </div>
        {/* Invisible range slider overlaid so it works with keyboard, mouse, and touch */}
        <input
          type="range"
          min="0"
          max="100"
          value={pos}
          onChange={(e) => setPos(Number(e.target.value))}
          className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
          aria-label="Drag to reveal before or after"
          data-testid="rehab-render-slider"
        />
      </div>
      <div className="mt-2 text-[11px] text-[var(--ink-3,#94A3B8)]">
        Drag the slider to compare. AI-generated visualization — not a
        construction spec. Rendered by iSpeedToClose · Gemini Nano Banana.
      </div>
    </div>
  );
}

